/**
 * Notification utilities for Shipment CoCaptain
 * Handles browser notifications and audio alerts for completed queries
 */

// Notification sound configuration
const NOTIFICATION_SOUND_URL = '/notification-sound.mp3'; // We'll add this sound file
const FALLBACK_NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBkCa2+/AiSOCz/Zs';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  silent?: boolean;
  playSound?: boolean;
}

class NotificationManager {
  private audioContext: AudioContext | null = null;
  private notificationSound: HTMLAudioElement | null = null;
  private isInitialized = false;
  private permission: NotificationPermission = 'default';

  constructor() {
    this.init();
  }

  /**
   * Initialize the notification manager
   */
  private async init() {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('Browser notifications are not supported');
      return;
    }

    // Get current permission status
    this.permission = Notification.permission;

    // Initialize audio for notification sounds
    this.initializeAudio();
    
    this.isInitialized = true;
  }

  /**
   * Initialize audio context and load notification sound
   */
  private initializeAudio() {
    try {
      // Create audio element for notification sound
      this.notificationSound = new Audio();
      
      // Try to load custom notification sound, fallback to default
      this.notificationSound.src = NOTIFICATION_SOUND_URL;
      this.notificationSound.volume = 0.3; // Set to 30% volume
      this.notificationSound.preload = 'auto';
      
      // Fallback to base64 encoded sound if custom sound fails
      this.notificationSound.onerror = () => {
        if (this.notificationSound) {
          this.notificationSound.src = FALLBACK_NOTIFICATION_SOUND;
        }
      };

      // Optional: Use Web Audio API for more control (if needed in future)
      if ('AudioContext' in window || 'webkitAudioContext' in window) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        this.audioContext = new AudioCtx();
      }
    } catch (error) {
      console.warn('Failed to initialize audio for notifications:', error);
    }
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Check if we should show notifications (only when user is away)
   */
  private shouldShowNotification(): boolean {
    // Don't show if notifications aren't supported or permission denied
    if (!this.isInitialized || this.permission !== 'granted') {
      return false;
    }

    // Check if the page/tab is currently visible
    // Only show notification if user has navigated away
    return document.hidden || !document.hasFocus();
  }

  /**
   * Play notification sound
   */
  private async playNotificationSound() {
    try {
      if (this.notificationSound) {
        // Reset the audio to start from beginning
        this.notificationSound.currentTime = 0;
        
        // Play the sound
        const playPromise = this.notificationSound.play();
        
        // Handle promise-based play() method
        if (playPromise !== undefined) {
          await playPromise;
        }
      }
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
      // Try fallback beep sound
      this.playFallbackSound();
    }
  }

  /**
   * Play a simple fallback beep sound using Web Audio API
   */
  private playFallbackSound() {
    try {
      if (this.audioContext) {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.5);
      }
    } catch (error) {
      console.warn('Failed to play fallback sound:', error);
    }
  }

  /**
   * Show a notification
   */
  async showNotification(options: NotificationOptions): Promise<Notification | null> {
    try {
      // Check if we should show the notification
      if (!this.shouldShowNotification()) {
        console.log('Skipping notification - user is currently active on the page');
        return null;
      }

      // Play sound if requested (even if notification isn't shown)
      if (options.playSound !== false) {
        await this.playNotificationSound();
      }

      // Create and show the notification
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/favicon-32x32.png', // Use app favicon as default
        tag: options.tag || 'shipment-cocaptain',
        silent: options.silent || false,
        requireInteraction: false, // Auto-close after a few seconds
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle notification click - focus the window
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      
      // Still try to play sound even if notification fails
      if (options.playSound !== false) {
        await this.playNotificationSound();
      }
      
      return null;
    }
  }

  /**
   * Show notification for query completion
   */
  async notifyQueryComplete(queryPreview: string, responsePreview: string): Promise<void> {
    const title = 'Shipment CoCaptain';
    const body = `Your query "${this.truncateText(queryPreview, 50)}" has completed. ${this.truncateText(responsePreview, 80)}`;
    
    await this.showNotification({
      title,
      body,
      tag: 'query-complete',
      playSound: true,
    });
  }

  /**
   * Show notification for errors
   */
  async notifyError(error: string): Promise<void> {
    const title = 'Shipment CoCaptain - Error';
    const body = `An error occurred: ${this.truncateText(error, 100)}`;
    
    await this.showNotification({
      title,
      body,
      tag: 'error',
      playSound: true,
    });
  }

  /**
   * Utility to truncate text for notifications
   */
  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Check if notifications are supported and get permission status
   */
  getStatus() {
    return {
      supported: 'Notification' in window,
      permission: this.permission,
      canNotify: this.permission === 'granted',
      initialized: this.isInitialized,
    };
  }
}

// Create singleton instance
export const notificationManager = new NotificationManager();

// Convenience functions
export const requestNotificationPermission = () => notificationManager.requestPermission();
export const notifyQueryComplete = (query: string, response: string) => 
  notificationManager.notifyQueryComplete(query, response);
export const notifyError = (error: string) => notificationManager.notifyError(error);
export const getNotificationStatus = () => notificationManager.getStatus();
